import { FC } from "react";
import { Box, Divider, Typography } from "@mui/material";

// Multi-lingual support
import { useTranslation } from "react-i18next";

// Types
import {
  EudrText,
  EUDR_STATUS_DESCRIPTION,
  EUDR_STATUS_DESCRIPTION_TITLE,
} from "./eudrStatusDescriptionContent";
import { IEudrStatusDescriptionProps } from "./EudrStatusDescriptionTypes";

/**
 * Static, read-only bilingual (TH/EN) rendering of the "Description of EUDR
 * Status" reference content. Designed to sit inside the Yield Config card body,
 * which is the screen's scroll container, so by default it flows with that
 * scroll rather than adding a nested scrollbar.
 *
 * The language follows the app language toggle: `useTranslation` re-renders on
 * `languageChanged`, so the whole block switches without a manual refresh.
 * Content is sourced solely from `eudrStatusDescriptionContent.ts` — no form
 * fields, no save, not editable.
 *
 * Pass `maxHeight` to give the block its own inner scroll; omit it (the default
 * when embedded) to let the surrounding container handle scrolling.
 */
const EudrStatusDescription: FC<IEudrStatusDescriptionProps> = ({
  maxHeight,
}) => {
  const { i18n } = useTranslation();
  // Thai is the default language; anything other than 'th' shows English.
  const lang: keyof EudrText = i18n.language === "th" ? "th" : "en";

  return (
    <Box component="section" sx={{ mt: 4 }}>
      <Divider sx={{ mb: 2 }} />

      {/* Section title */}
      <Typography
        sx={{ color: "risinblack.main", fontWeight: 600, fontSize: 18, mb: 2 }}
      >
        {EUDR_STATUS_DESCRIPTION_TITLE[lang]}
      </Typography>

      {/* Content flows naturally so the whole Yield Config section scrolls as
          one (per the client). A `maxHeight` prop still gives the block its own
          inner scroll for standalone use. */}
      <Box sx={maxHeight ? { maxHeight, overflowY: "auto", pr: 1 } : undefined}>
        {EUDR_STATUS_DESCRIPTION.map((section) => (
          <Box component="section" key={section.id} sx={{ mb: 4 }}>
            {section.title && (
              <Typography
                variant="h6"
                sx={{
                  color: "risinblack.main",
                  fontWeight: 600,
                  fontSize: 16,
                  mb: section.description ? 0.5 : 1.5,
                }}
              >
                {section.title[lang]}
              </Typography>
            )}

            {section.description && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                {section.description[lang]}
              </Typography>
            )}

            {section.subsections.map((subsection) => (
              <Box
                key={subsection.code}
                sx={{
                  pl: 2,
                  mb: 3,
                  borderLeft: "2px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "risinblack.main",
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  {`${subsection.no[lang]} ${subsection.code}`}
                </Typography>

                {subsection.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      mb: subsection.options ? 1 : 0,
                    }}
                  >
                    {subsection.description[lang]}
                  </Typography>
                )}

                {subsection.options && (
                  <Box component="ol" sx={{ listStyle: "none", pl: 0, m: 0 }}>
                    {subsection.options.map((option) => (
                      <Box component="li" key={option.label} sx={{ mb: 0.75 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary" }}
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {option.label}
                          </Box>
                          {" — "}
                          {option.text[lang]}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {subsection.note && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1,
                      color: "text.secondary",
                      fontStyle: "italic",
                    }}
                  >
                    {subsection.note[lang]}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EudrStatusDescription;
